import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { InvoiceItem } from '@/features/invoices/services/generateInvoicePdf';
import type { Json } from '@/integrations/supabase/types';

export interface Quote {
  id: string;
  owner_id: string;
  invoice_number: string;
  client_id: string | null;
  client_name: string;
  client_email: string;
  client_address: Record<string, string>;
  items: InvoiceItem[];
  total_ht: number;
  total_vat: number;
  total_ttc: number;
  deposit_amount: number;
  status: QuoteStatus;
  issue_date: string;
  due_date: string | null;
  notes: string;
  converted_from_quote_id: string | null;
  created_at: string;
  updated_at: string;
}

export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'refused';

export const QUOTE_STATUSES: { value: QuoteStatus; label: Record<'fr' | 'en' | 'es', string> }[] = [
  { value: 'draft',    label: { fr: 'Brouillon', en: 'Draft',    es: 'Borrador'  } },
  { value: 'sent',     label: { fr: 'Envoyé',    en: 'Sent',     es: 'Enviado'   } },
  { value: 'accepted', label: { fr: 'Accepté',   en: 'Accepted', es: 'Aceptado'  } },
  { value: 'refused',  label: { fr: 'Refusé',    en: 'Refused',  es: 'Rechazado' } },
];

export function useQuotes() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['quotes', user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('invoices')
        .select('*')
        .eq('owner_id', user!.id)
        .eq('type', 'quote')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []).map((d: any) => ({
        ...d,
        items: (d.items as unknown as InvoiceItem[]) || [],
        client_address: (d.client_address as unknown as Record<string, string>) || {},
        deposit_amount: d.deposit_amount || 0,
      })) as Quote[];
    },
    enabled: !!user,
  });
}

export function useNextQuoteNumber() {
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (): Promise<string> => {
      if (!user) throw new Error('Not authenticated');
      const year = new Date().getFullYear();

      const { data: existing } = await (supabase as any)
        .from('invoice_counters')
        .select('*')
        .eq('owner_id', user.id)
        .eq('doc_type', 'quote')
        .maybeSingle();

      let nextNum: number;

      if (!existing) {
        nextNum = 1;
        await (supabase as any).from('invoice_counters').insert({
          owner_id: user.id,
          doc_type: 'quote',
          current_year: year,
          current_number: 1,
        });
      } else if (existing.current_year !== year) {
        nextNum = 1;
        await (supabase as any)
          .from('invoice_counters')
          .update({ current_year: year, current_number: 1 })
          .eq('owner_id', user.id)
          .eq('doc_type', 'quote');
      } else {
        nextNum = existing.current_number + 1;
        await (supabase as any)
          .from('invoice_counters')
          .update({ current_number: nextNum })
          .eq('owner_id', user.id)
          .eq('doc_type', 'quote');
      }

      return `DEV-${year}-${String(nextNum).padStart(4, '0')}`;
    },
  });
}

export function useSaveQuote() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      quote: Partial<Quote> & {
        invoice_number: string;
        client_name: string;
        items: InvoiceItem[];
      },
    ) => {
      const row = {
        type: 'quote',
        invoice_number: quote.invoice_number,
        amount: quote.total_ttc || 0,
        client_id: quote.client_id || null,
        client_name: quote.client_name,
        client_email: quote.client_email || '',
        client_address: (quote.client_address || {}) as unknown as Json,
        items: quote.items as unknown as Json,
        total_ht: quote.total_ht || 0,
        total_vat: quote.total_vat || 0,
        total_ttc: quote.total_ttc || 0,
        deposit_amount: quote.deposit_amount || 0,
        status: quote.status || 'draft',
        issue_date: quote.issue_date || new Date().toISOString().split('T')[0],
        due_date: quote.due_date || null,
        notes: quote.notes || '',
      };

      if (quote.id) {
        const { error } = await (supabase as any)
          .from('invoices')
          .update(row)
          .eq('id', quote.id);
        if (error) throw error;
      } else {
        const { error } = await (supabase as any)
          .from('invoices')
          .insert({ ...row, owner_id: user!.id });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from('invoices').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

export function useUpdateQuoteStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: QuoteStatus }) => {
      const { error } = await (supabase as any)
        .from('invoices')
        .update({ status })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

export function useConvertToInvoice() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ quote, invoiceNumber, sessionId }: { quote: Quote; invoiceNumber: string; sessionId?: string | null }) => {
      const { error } = await (supabase as any).from('invoices').insert({
        owner_id: user!.id,
        type: 'invoice',
        invoice_number: invoiceNumber,
        session_id: sessionId || null,
        client_id: quote.client_id,
        client_name: quote.client_name,
        client_email: quote.client_email,
        client_address: quote.client_address as unknown as Json,
        items: quote.items as unknown as Json,
        total_ht: quote.total_ht,
        total_vat: quote.total_vat,
        total_ttc: quote.total_ttc,
        amount: quote.total_ttc,
        deposit_amount: quote.deposit_amount,
        status: 'draft',
        issue_date: new Date().toISOString().split('T')[0],
        notes: quote.notes,
        converted_from_quote_id: quote.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
